import { supabase } from '../../utils/supabase';

export interface DataAuditResult {
  player_number: number;
  player_name: string;
  raw_games: number;
  raw_score: number;
  raw_wins: number;
  leaderboard_games: number;
  leaderboard_score: number;
  leaderboard_wins: number;
  game_diff: number;
  score_diff: number;
  wins_diff: number;
  status: '✅ 一致' | '❌ 差异';
}

/**
 * V106: 数据审计服务 - 完全对齐统计逻辑
 */
export class DataAuditService {
  /**
   * V106: 全量数据核对
   * 使用与 loadPlayerStats 完全相同的查询和计算逻辑
   */
  async runFullAudit(dojoFilter: string = 'all', seasonFilter: string = 'all'): Promise<{
    results: DataAuditResult[];
    summary: {
      totalPlayers: number;
      consistentPlayers: number;
      inconsistentPlayers: number;
      totalGameDiff: number;
      totalScoreDiff: number;
    };
  }> {
    console.log('[V106 DataAudit] 开始全量数据核对...');
    console.log('[V106 DataAudit] 过滤器:', { dojoFilter, seasonFilter });

    // V106: 步骤 1 - 使用与 dataService 相同的查询方法获取原始数据
    const rawStats = await this.calculateRawStats(dojoFilter, seasonFilter);
    console.log('[V106 DataAudit] 原始数据统计完成:', Object.keys(rawStats).length, '位玩家');

    // V106: 步骤 2 - 使用与 dataService 相同的逻辑从 player_stats 获取缓存数据
    const leaderboardStats = await this.getLeaderboardStats();
    console.log('[V106 DataAudit] 积分榜数据获取完成:', Object.keys(leaderboardStats).length, '位玩家');

    // V106: 步骤 3 - 对比并生成报告
    const results: DataAuditResult[] = [];
    const allPlayerNumbers = new Set([
      ...Object.keys(rawStats).map(Number),
      ...Object.keys(leaderboardStats).map(Number),
    ]);

    for (const playerNum of allPlayerNumbers) {
      const raw = rawStats[playerNum] || { total_games: 0, total_score: 0, wins: 0, player_name: `${playerNum}号` };
      const lb = leaderboardStats[playerNum] || { total_games: 0, total_score: 0, wins: 0, player_name: raw.player_name };

      const gameDiff = (lb.total_games || 0) - (raw.total_games || 0);
      const scoreDiff = (lb.total_score || 0) - (raw.total_score || 0);
      const winsDiff = (lb.wins || 0) - (raw.wins || 0);

      // V106: 使用更严格的比较（考虑浮点数精度）
      const hasDiff = Math.abs(gameDiff) > 0 || Math.abs(scoreDiff) > 0.01 || Math.abs(winsDiff) > 0;

      results.push({
        player_number: playerNum,
        player_name: raw.player_name || lb.player_name || `${playerNum}号`,
        raw_games: raw.total_games || 0,
        raw_score: Math.round((raw.total_score || 0) * 100) / 100,
        raw_wins: raw.wins || 0,
        leaderboard_games: lb.total_games || 0,
        leaderboard_score: Math.round((lb.total_score || 0) * 100) / 100,
        leaderboard_wins: lb.wins || 0,
        game_diff: gameDiff,
        score_diff: Math.round(scoreDiff * 100) / 100,
        wins_diff: winsDiff,
        status: hasDiff ? '❌ 差异' : '✅ 一致',
      });
    }

    // 按差异大小排序
    results.sort((a, b) => Math.abs(b.score_diff) - Math.abs(a.score_diff));

    const inconsistentPlayers = results.filter(r => r.status === '❌ 差异');
    const summary = {
      totalPlayers: results.length,
      consistentPlayers: results.filter(r => r.status === '✅ 一致').length,
      inconsistentPlayers: inconsistentPlayers.length,
      totalGameDiff: inconsistentPlayers.reduce((sum, r) => sum + Math.abs(r.game_diff), 0),
      totalScoreDiff: inconsistentPlayers.reduce((sum, r) => sum + Math.abs(r.score_diff), 0),
    };

    console.log('[V106 DataAudit] 核对完成:', summary);

    return { results, summary };
  }

  /**
   * V106: 计算原始统计数据
   * 与 dataService.loadPlayerStats 使用完全相同的查询和聚合逻辑
   */
  private async calculateRawStats(dojoFilter: string, seasonFilter: string): Promise<Record<number, any>> {
    // V106: 与 dataService.fetchRawPlayerRecords 相同的查询
    let query = supabase
      .from('player_round_records')
      .select('*, game_records!inner(*)')
      .eq('is_deleted', 'n')
      .eq('status', 'valid')
      .eq('game_records.is_deleted', 'n')
      .eq('game_records.status', 'valid');

    if (dojoFilter !== 'all') {
      query = query.eq('game_records.dojo_id', Number(dojoFilter));
    }
    if (seasonFilter !== 'all') {
      query = query.eq('game_records.season_id', Number(seasonFilter));
    }

    const { data: records, error } = await query;

    if (error) {
      console.error('[V106 DataAudit] 查询原始数据失败:', error);
      throw error;
    }

    console.log(`[V106 DataAudit] 查询到 ${records?.length || 0} 条原始记录`);

    // V106: 与 dataService.aggregatePlayerStats 相同的去重逻辑
    const uniqueRecordsMap = new Map<string, any>();
    const duplicateKeys: string[] = [];

    for (const record of records || []) {
      const key = `${record.player_number}_${record.game_record_id}`;
      if (uniqueRecordsMap.has(key)) {
        duplicateKeys.push(key);
      } else {
        uniqueRecordsMap.set(key, record);
      }
    }

    if (duplicateKeys.length > 0) {
      console.warn(`[V106 DataAudit] 发现 ${duplicateKeys.length} 条重复记录:`, duplicateKeys.slice(0, 10));
    }

    // V106: 与 dataService.aggregatePlayerStats 相同的聚合逻辑
    const statsMap: Record<number, any> = {};

    for (const record of uniqueRecordsMap.values()) {
      const playerNum = record.player_number;

      if (!statsMap[playerNum]) {
        statsMap[playerNum] = {
          player_number: playerNum,
          player_name: record.player_nickname || `${playerNum}号`,
          total_games: 0,
          total_score: 0,
          wins: 0,
        };
      }

      const stats = statsMap[playerNum];
      const scoreChange = Number(record.score_change) || 0;
      const isWin = scoreChange > 0;

      stats.total_games += 1;
      stats.total_score += scoreChange;
      stats.wins += isWin ? 1 : 0;
    }

    return statsMap;
  }

  /**
   * V106: 从 player_stats 表获取缓存数据
   */
  private async getLeaderboardStats(): Promise<Record<number, any>> {
    const { data: stats, error } = await supabase
      .from('player_stats')
      .select('*');

    if (error) {
      console.error('[V106 DataAudit] 查询积分榜失败:', error);
      throw error;
    }

    const statsMap: Record<number, any> = {};

    for (const stat of stats || []) {
      statsMap[stat.player_number] = {
        player_number: stat.player_number,
        player_name: stat.player_name || `${stat.player_number}号`,
        total_games: stat.total_games || 0,
        total_score: stat.total_score || 0,
        wins: stat.wins || 0,
      };
    }

    return statsMap;
  }

  /**
   * V106: 生成审计报告
   */
  generateAuditReport(results: DataAuditResult[], summary: any): string {
    const lines: string[] = [];
    lines.push('═══════════════════════════════════════════════════════════════');
    lines.push('                    [V106 数据核对报告]');
    lines.push('═══════════════════════════════════════════════════════════════');
    lines.push('');
    lines.push('【汇总信息】');
    lines.push(`  总玩家数: ${summary.totalPlayers}`);
    lines.push(`  数据一致: ${summary.consistentPlayers} 位`);
    lines.push(`  数据差异: ${summary.inconsistentPlayers} 位`);
    lines.push(`  总局数差异: ${summary.totalGameDiff}`);
    lines.push(`  总积分差异: ${summary.totalScoreDiff}`);
    lines.push('');

    const inconsistent = results.filter(r => r.status === '❌ 差异');
    if (inconsistent.length > 0) {
      lines.push('【差异详情】');
      lines.push('玩家编号 | 玩家昵称 | 原始对局 | 榜单对局 | 局数差 | 原始积分 | 榜单积分 | 积分差');
      lines.push('─────────┼─────────┼─────────┼─────────┼───────┼─────────┼─────────┼───────');
      
      for (const r of inconsistent) {
        lines.push(
          `${r.player_number.toString().padStart(4)}号 | ` +
          `${r.player_name.padEnd(6)} | ` +
          `${r.raw_games.toString().padStart(4)}局 | ` +
          `${r.leaderboard_games.toString().padStart(4)}局 | ` +
          `${r.game_diff >= 0 ? '+' : ''}${r.game_diff.toString().padStart(3)} | ` +
          `${r.raw_score.toString().padStart(6)}分 | ` +
          `${r.leaderboard_score.toString().padStart(6)}分 | ` +
          `${r.score_diff >= 0 ? '+' : ''}${r.score_diff.toString().padStart(6)}`
        );
      }
    } else {
      lines.push('✅ 所有玩家数据一致！');
    }

    lines.push('');
    lines.push('═══════════════════════════════════════════════════════════════');

    return lines.join('\n');
  }
}

export const dataAuditService = new DataAuditService();
